import { BrowserRouter, Routes, Route, Navigate } from 'react-router'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { AuthProvider, RequireAuth } from '@/lib/auth'
import { isSupabaseConfigured } from '@/lib/supabase/client'

// Layouts
import { AuthLayout, AppLayout } from '@/layouts'

// Auth routes
import { LoginPage } from '@/routes/auth/login'
import { SignupPage } from '@/routes/auth/signup'
import { TeamSetupPage } from '@/routes/auth/team-setup'
import { CalendarSetupPage } from '@/routes/auth/calendar-setup'
import { AuthCallbackPage } from '@/routes/auth/callback'

// App routes
import { PipelinePage } from '@/routes/app/pipeline'
import { RecordingsPage } from '@/routes/app/recordings'
import { RecordingDetailPage } from '@/routes/app/recording-detail'
import { SeriesPage } from '@/routes/app/series'
import { SeriesDetailPage } from '@/routes/app/series-detail'
import { GuestsPage } from '@/routes/app/guests'
import { GuestDetailPage } from '@/routes/app/guest-detail'
import { SchedulePage } from '@/routes/app/schedule'
import { IntegrationsPage } from '@/routes/app/integrations'
import { SettingsPage } from '@/routes/app/settings'
import { BillingPage } from '@/routes/app/billing'
import { HelpPage } from '@/routes/app/help'
import { PricingPage } from '@/routes/app/pricing'

// Pages
import { HomePage } from '@/routes/home'
import { SetupPage } from '@/routes/setup'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
    },
  },
})

export function App() {
  // Show setup page if Supabase is not configured
  if (!isSupabaseConfigured()) {
    return <SetupPage />
  }

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            {/* Public auth routes */}
            <Route element={<AuthLayout />}>
              <Route path="login" element={<LoginPage />} />
              <Route path="signup" element={<SignupPage />} />
              <Route path="team-setup" element={<TeamSetupPage />} />
              <Route path="calendar-setup" element={<CalendarSetupPage />} />
            </Route>

            {/* OAuth callback - no layout */}
            <Route path="auth/callback" element={<AuthCallbackPage />} />

            {/* Protected app routes */}
            <Route
              element={
                <RequireAuth>
                  <AppLayout />
                </RequireAuth>
              }
            >
              <Route path="pipeline" element={<PipelinePage />} />
              <Route path="recordings" element={<RecordingsPage />} />
              <Route path="recordings/:id" element={<RecordingDetailPage />} />
              <Route path="collections" element={<SeriesPage />} />
              <Route path="collections/:id" element={<SeriesDetailPage />} />
              <Route path="guests" element={<GuestsPage />} />
              <Route path="guests/:id" element={<GuestDetailPage />} />
              <Route path="schedule" element={<SchedulePage />} />
              <Route path="integrations" element={<IntegrationsPage />} />
              <Route path="settings" element={<SettingsPage />} />
              <Route path="billing" element={<BillingPage />} />
              <Route path="pricing" element={<PricingPage />} />
              <Route path="help" element={<HelpPage />} />
            </Route>

            {/* Home - redirects based on auth */}
            <Route path="/" element={<HomePage />} />

            {/* Catch-all redirect */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  )
}
